/**
 * OneDrive File Picker v8 — opens a popup for the user to browse OneDrive/SharePoint
 * and returns the selected file's share link.
 *
 * Requires the M365 app registration to have:
 * - SPA redirect URI matching the frontend origin
 * - Files.Read.All delegated permission
 */
import { PublicClientApplication, SilentRequest } from '@azure/msal-browser';

const M365_TENANT_ID = '9fc32148-cbde-48e6-a539-5070818f2fa9';
const M365_CLIENT_ID = 'e9fc9af5-cb37-4bb3-a704-63fbb8088b90';
const REDIRECT_URI = window.location.origin;

const msalConfig = {
  auth: {
    clientId: M365_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${M365_TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage' as const,
  },
};

let msalInstance: PublicClientApplication | null = null;

async function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
}

async function getSharePointToken(resource: string): Promise<string> {
  const msal = await getMsalInstance();
  const accounts = msal.getAllAccounts();
  // Use .default scope which includes all granted permissions for the resource
  const scopes = [`${resource.replace(/\/$/, '')}/.default`];

  if (accounts.length > 0) {
    try {
      const result = await msal.acquireTokenSilent({ scopes, account: accounts[0] });
      return result.accessToken;
    } catch {
      // fall through to popup
    }
  }

  const result = await msal.acquireTokenPopup({ scopes });
  if (result.account) {
    msal.setActiveAccount(result.account);
  }
  return result.accessToken;
}

export interface OneDrivePickResult {
  name: string;
  url: string;
  webUrl?: string;
  driveId?: string;
  itemId?: string;
}

/**
 * Opens the OneDrive File Picker v8 in a popup and returns the picked file info.
 * Returns null if the user cancels.
 */
export async function openOneDrivePicker(): Promise<OneDrivePickResult | null> {
  // Determine the user's OneDrive base URL
  const baseUrl = `https://uniquelandscapes-my.sharepoint.com`;

  // Get initial auth token for the SharePoint resource
  const token = await getSharePointToken(baseUrl);

  return new Promise<OneDrivePickResult | null>((resolve) => {
    // Open the picker popup
    const win = window.open('', 'OneDrivePicker', 'width=1080,height=680');
    if (!win) {
      alert('Popup blocked — please allow popups for this site.');
      resolve(null);
      return;
    }

    const channelId = crypto.randomUUID();

    const options = {
      sdk: '8.0',
      entry: {
        oneDrive: {
          files: {},
        },
      },
      authentication: {},
      messaging: {
        origin: window.location.origin,
        channelId,
      },
      selection: {
        mode: 'single',
      },
    };

    const queryString = new URLSearchParams({
      filePicker: JSON.stringify(options),
      locale: 'en-us',
    });

    const url = `${baseUrl}/_layouts/15/FilePicker.aspx?${queryString}`;

    // Create form and POST to the picker URL
    const form = win.document.createElement('form');
    form.setAttribute('action', url);
    form.setAttribute('method', 'POST');

    const tokenInput = win.document.createElement('input');
    tokenInput.setAttribute('type', 'hidden');
    tokenInput.setAttribute('name', 'access_token');
    tokenInput.setAttribute('value', token);
    form.appendChild(tokenInput);

    win.document.body.append(form);
    form.submit();

    let port: MessagePort | null = null;
    let resolved = false;

    function cleanup() {
      window.removeEventListener('message', messageListener);
      if (port) port.close();
    }

    // Listen for the picker's initialize message
    function messageListener(event: MessageEvent) {
      if (event.source !== win) return;
      const message = event.data;

      if (message.type === 'initialize' && message.channelId === channelId) {
        port = event.ports[0];
        port.addEventListener('message', channelListener);
        port.start();
        port.postMessage({ type: 'activate' });
      }
    }

    async function channelListener(event: MessageEvent) {
      const payload = event.data;

      if (payload.type === 'notification') {
        // ignore notifications
        return;
      }

      if (payload.type === 'command') {
        // Acknowledge all commands
        port!.postMessage({ type: 'acknowledge', id: payload.id });

        const command = payload.data;

        switch (command.command) {
          case 'authenticate':
            try {
              const authToken = await getSharePointToken(command.resource);
              port!.postMessage({
                type: 'result',
                id: payload.id,
                data: { result: 'token', token: authToken },
              });
            } catch (err: any) {
              port!.postMessage({
                type: 'result',
                id: payload.id,
                data: { result: 'error', error: { code: 'unableToObtainToken', message: err.message } },
              });
            }
            break;

          case 'pick':
            // User picked a file
            const items = command.items || [];
            if (items.length > 0) {
              const picked = items[0];
              const endpoint = picked['@sharePoint.endpoint'] || '';
              const driveId = picked.parentReference?.driveId || '';
              const itemId = picked.id || '';

              // Get a sharing link for the picked file
              let shareUrl = '';
              try {
                const linkToken = await getSharePointToken(endpoint || baseUrl);
                const apiUrl = `${endpoint}/drives/${driveId}/items/${itemId}/createLink`;
                const linkResp = await fetch(apiUrl, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${linkToken}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ type: 'view', scope: 'organization' }),
                });
                if (linkResp.ok) {
                  const linkData = await linkResp.json();
                  shareUrl = linkData.link?.webUrl || '';
                }
              } catch {
                // Fall back to webUrl if available
              }

              // If no share link, try the webUrl from the item
              if (!shareUrl && picked.webUrl) {
                shareUrl = picked.webUrl;
              }

              resolved = true;
              resolve({
                name: picked.name || 'Untitled',
                url: shareUrl || picked.webUrl || '',
                webUrl: picked.webUrl,
                driveId,
                itemId,
              });
            }

            port!.postMessage({
              type: 'result',
              id: payload.id,
              data: { result: 'success' },
            });

            // Close the popup
            setTimeout(() => { try { win?.close(); } catch {} }, 500);
            cleanup();
            break;

          case 'close':
            // User closed the picker
            if (!resolved) resolve(null);
            try { win?.close(); } catch {}
            cleanup();
            break;

          default:
            port!.postMessage({
              type: 'result',
              id: payload.id,
              data: { result: 'error', error: { code: 'unsupportedCommand', message: command.command } },
            });
            break;
        }
      }
    }

    window.addEventListener('message', messageListener);

    // Handle popup being closed manually
    const pollClosed = setInterval(() => {
      if (win?.closed) {
        clearInterval(pollClosed);
        if (!resolved) resolve(null);
        cleanup();
      }
    }, 1000);
  });
}
